import { useState } from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import { isLateNight, getEffectiveDayIndex } from '@/contexts/FilterContext';

// Google Places API hours format
interface GoogleHours {
  periods?: Array<{
    open: { day: number; hour: number; minute: number };
    close?: { day: number; hour: number; minute: number };
  }>;
  weekdayDescriptions?: string[];
}

interface HoursExpanderProps {
  hours: GoogleHours;
}

// Map day index (0=Sunday) to day name for matching
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function HoursExpander({ hours }: HoursExpanderProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get current day of week (0 = Sunday), accounting for 3am rollover
  const todayIndex = getEffectiveDayIndex();
  const todayName = DAY_NAMES[todayIndex];

  // Use weekdayDescriptions if available (e.g., "Monday: 11:00 AM – 2:00 AM")
  const descriptions = hours.weekdayDescriptions || [];

  // Find today's hours from descriptions
  const findTodayHours = (): string => {
    const todayDesc = descriptions.find((desc) =>
      desc.toLowerCase().startsWith(todayName.toLowerCase())
    );
    if (todayDesc) {
      // Extract the hours part after the colon
      const colonIndex = todayDesc.indexOf(':');
      if (colonIndex !== -1) {
        return todayDesc.slice(colonIndex + 1).trim();
      }
    }
    return 'Closed';
  };

  const todayDisplay = findTodayHours();

  // Reorder descriptions to start from today
  const getOrderedDescriptions = () => {
    if (descriptions.length === 0) return [];

    // Google usually returns Monday-Sunday, but we want to show starting from today
    // First, let's find the index of today in the descriptions
    const todayDescIndex = descriptions.findIndex((desc) =>
      desc.toLowerCase().startsWith(todayName.toLowerCase())
    );

    if (todayDescIndex === -1) return descriptions;

    // Reorder to start from today
    return [
      ...descriptions.slice(todayDescIndex),
      ...descriptions.slice(0, todayDescIndex),
    ];
  };

  const orderedDescriptions = getOrderedDescriptions();

  // Parse a description to get day name and hours
  const parseDescription = (desc: string): { day: string; hours: string } => {
    const colonIndex = desc.indexOf(':');
    if (colonIndex !== -1) {
      return {
        day: desc.slice(0, colonIndex).trim(),
        hours: desc.slice(colonIndex + 1).trim(),
      };
    }
    return { day: desc, hours: '' };
  };

  if (descriptions.length === 0) {
    return null;
  }

  return (
    <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-[var(--color-card)] hover:bg-[var(--color-border)] transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="h-4 w-4 text-[var(--color-muted)] shrink-0" />
          <span className="font-medium shrink-0">{isLateNight() ? '\u201CToday\u201D' : 'Today'}</span>
          <span className="text-[var(--color-muted)] truncate">{todayDisplay}</span>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {isExpanded && (
        <div className="p-3 space-y-1 border-t border-[var(--color-border)]">
          {orderedDescriptions.map((desc, index) => {
            const { day, hours: hoursText } = parseDescription(desc);
            const isToday = index === 0;
            return (
              <div
                key={day}
                className={`flex justify-between text-sm py-0.5 gap-4 ${
                  isToday ? 'font-medium' : ''
                }`}
              >
                <span className={`shrink-0 ${isToday ? '' : 'text-[var(--color-muted)]'}`}>
                  {day}
                </span>
                <span className={`text-right ${isToday ? '' : 'text-[var(--color-muted)]'}`}>
                  {hoursText}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
